import { useState } from "react";
import { toast } from "sonner";
import { Trash2, UserPlus } from "lucide-react";
import { adminApi } from "@/lib/auth";
import { errorText } from "@/lib/errors";
import { Loading, useAdminList } from "./shared";

const input = "mt-1 block w-full bg-transparent border-b border-pine/40 py-2 text-base focus:outline-none focus:border-brass transition-colors";

const AddMember = ({ onAdded }) => {
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await adminApi.post("/admin/team", form);
      toast.success(`${form.name} can now sign in. Share the password with them privately.`);
      setForm({ name: "", email: "", password: "" });
      onAdded();
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <form onSubmit={submit} className="bg-paper rounded-2xl p-6 grid sm:grid-cols-3 gap-4" data-testid="team-add-form">
      <p className="sm:col-span-3 lb text-brass-ink">Add someone, like Don</p>
      <label className="text-sm">Name<input required value={form.name} onChange={set("name")} className={input} data-testid="team-name-input" /></label>
      <label className="text-sm">Email<input required type="email" value={form.email} onChange={set("email")} className={input} data-testid="team-email-input" /></label>
      <label className="text-sm">Starting password (8+)<input required minLength={8} value={form.password} onChange={set("password")} className={input} data-testid="team-password-input" /></label>
      <button type="submit" disabled={saving} className="sm:col-span-3 justify-self-start inline-flex items-center gap-2 rounded-full bg-pine text-sage px-6 py-3 disabled:opacity-70" data-testid="team-add-button"><UserPlus size={16} /> {saving ? "Adding..." : "Give them a sign-in"}</button>
    </form>
  );
};

const ChangePassword = () => {
  const [form, setForm] = useState({ current_password: "", new_password: "" });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await adminApi.post("/auth/change-password", form);
      toast.success("Your password has been changed.");
      setForm({ current_password: "", new_password: "" });
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <form onSubmit={submit} className="bg-paper rounded-2xl p-6 grid sm:grid-cols-2 gap-4" data-testid="change-password-form">
      <p className="sm:col-span-2 lb text-brass-ink">Change your own password</p>
      <label className="text-sm">Current password<input required type="password" value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} className={input} data-testid="current-password-input" /></label>
      <label className="text-sm">New password (8+)<input required minLength={8} type="password" value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} className={input} data-testid="new-password-input" /></label>
      <button type="submit" disabled={saving} className="sm:col-span-2 justify-self-start rounded-full border border-pine px-6 py-3 disabled:opacity-70" data-testid="change-password-button">{saving ? "Saving..." : "Change password"}</button>
    </form>
  );
};

export const TeamTab = () => {
  const { items, reload } = useAdminList("/admin/team");
  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.name || m.email}'s sign-in?`)) return;
    try { await adminApi.delete(`/admin/team/${m.id}`); toast.success("Sign-in removed."); reload(); } catch (err) { toast.error(errorText(err)); }
  };
  return (
    <div className="grid gap-6">
      <p className="text-moss max-w-2xl">Everyone listed here can open this private inbox, including prayer requests.</p>
      {!items ? <Loading /> : (
        <ul className="grid gap-3" data-testid="team-list">
          {items.map((m) => (
            <li key={m.id} className="bg-paper rounded-2xl px-6 py-4 flex justify-between items-center gap-4" data-testid={`team-member-${m.id}`}>
              <span><span className="font-medium">{m.name || "Team member"}</span> · <span className="text-sm text-moss break-all">{m.email}</span>{m.is_you && <span className="ml-2 lb text-brass-ink">You</span>}</span>
              {!m.is_you && <button type="button" onClick={() => remove(m)} className="inline-flex items-center gap-1 text-sm text-moss hover:text-red-800" data-testid={`team-remove-${m.id}`}><Trash2 size={14} /> Remove</button>}
            </li>
          ))}
        </ul>
      )}
      <AddMember onAdded={reload} />
      <ChangePassword />
    </div>
  );
};
